import apiClient from '@/lib/api-client';

/** Taal-neutrale (ISO/systeemcode) shapes exactly as returned by the h3-1 orders BFF module. */
export type ServiceState = 'active' | 'onhold' | 'canceled' | 'completed';

export interface Progress {
  currentStep: string | null;
  currentStepNumber: number | null;
  totalSteps: number;
}

export interface OrderServiceAggregate {
  serviceCode: string;
  state: ServiceState;
  serviceStatuses: string[];
  progress: Progress | null;
  lastEventAt: string | null;
}

export interface OrderAggregate {
  orderNumber: string;
  gln: string | null;
  serviceCodes: string[];
  productCount: number;
  status: ServiceState;
  createdAt: string | null;
  firstEventAt: string | null;
  lastEventAt: string | null;
  leadTimeMs: number | null;
  leadTimeBasis: 'now' | 'lastEvent' | null;
  services: OrderServiceAggregate[];
  /** fu-6/AC1: portal-only enrichment (batch GLN->naam lookup). Falls back to `gln` in the UI. */
  companyName?: string | null;
}

export interface OrdersListMeta {
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
  degraded: boolean;
}

export interface OrdersListEnvelope {
  data: OrderAggregate[] | null;
  meta: OrdersListMeta;
}

export interface OrdersListParams {
  gln?: string;
  serviceCode?: string;
  status?: string;
  from?: string;
  to?: string;
  lastEventBefore?: string;
  attention?: boolean;
  stalenessDays?: number;
  /** fu-6/AC5: vrij zoeken op ordernummer, klantnaam of klantnummer. */
  search?: string;
  page?: number;
  pageSize?: number;
  sort?: string;
}

export interface OrderSettings {
  orderStalenessDays: number;
}

export interface TreeServiceView {
  serviceCode: string;
  serviceStatus: string | null;
  state: ServiceState;
  progress: Progress | null;
  lastEvent: { processCode: string; eventCode: string; timestamp: string } | null;
}

export interface PackagingLayer {
  gtin: string;
  tradeItemId: string | null;
  tradeItemUnitDescriptor: string | null;
  description: string | null;
  referencedTradeItem: string | null;
  quantityOfChildren: number | null;
  source: 'tradeItem' | 'articleHierarchy';
  services: TreeServiceView[];
}

export interface ProductNode {
  tradeItemId: string;
  gtin: string;
  gln: string;
  targetMarket: string;
  description: string | null;
  tradeItemUnitDescriptor: string | null;
  services: TreeServiceView[];
  packagingLayers: PackagingLayer[];
}

export interface OrderTree {
  orderNumber: string;
  gln: string | null;
  status: ServiceState;
  products: ProductNode[];
  /** fu-7: resolved via glnProvider.getCompanyName server-side; null when unresolved/unavailable — the header falls back to the GLN. */
  companyName?: string | null;
}

export interface OrderTreeMeta {
  degraded: boolean;
}

export interface OrderTreeEnvelope {
  data: OrderTree | null;
  meta: OrderTreeMeta;
}

export interface TimelineEvent {
  id: number;
  tradeItemId: string;
  serviceCode: string;
  processCode: string;
  eventCode: string;
  // Kan ontbreken: de bron laat dit veld weg op een meetrij van de behandeltijd (besluit DW-34,
  // 21-08-2026). Zie de toelichting bij timelineEventSchema in de achterkant.
  userId: number | null;
  timestamp: string;
  serviceId: number | null;
}

export interface TimelineMeta {
  degraded: boolean;
  total: number | null;
  hasMore: boolean | null;
}

export interface TimelineEnvelope {
  data: TimelineEvent[] | null;
  meta: TimelineMeta;
}

/** Lists orders for the internal orderlijst (h3-2, employee/admin). */
export async function getOrders(params: OrdersListParams = {}): Promise<OrdersListEnvelope> {
  const query = new URLSearchParams();
  if (params.gln != null) query.set('gln', params.gln);
  if (params.serviceCode != null) query.set('serviceCode', params.serviceCode);
  if (params.status != null) query.set('status', params.status);
  if (params.from != null) query.set('from', params.from);
  if (params.to != null) query.set('to', params.to);
  if (params.lastEventBefore != null) query.set('lastEventBefore', params.lastEventBefore);
  if (params.attention) query.set('attention', 'true');
  if (params.stalenessDays != null) query.set('stalenessDays', String(params.stalenessDays));
  // Lege of alleen-spaties zoektermen niet meesturen: de achterkant weigert die (min 1 na trim),
  // en het is bovendien geen filter — het zou de hele lijst als "gefilterd" laten lezen.
  if (params.search != null && params.search.trim() !== '') query.set('search', params.search.trim());
  if (params.sort != null) query.set('sort', params.sort);
  query.set('page', String(params.page ?? 1));
  query.set('pageSize', String(params.pageSize ?? 25));

  const response = await apiClient.get<OrdersListEnvelope>(`/orders?${query.toString()}`);
  return response.data;
}

/** FR16 default staleness threshold (h3-6 will make this admin-editable; read-only here). */
export async function getOrderSettings(): Promise<OrderSettings> {
  const response = await apiClient.get<{ data: OrderSettings }>('/orders/settings');
  return response.data.data;
}

/** Order-boom (FR17): order -> products -> verpakkingslagen, per product beide statussen. */
export async function getOrderTree(orderNumber: string): Promise<OrderTreeEnvelope> {
  const response = await apiClient.get<OrderTreeEnvelope>(`/orders/${encodeURIComponent(orderNumber)}/tree`);
  return response.data;
}

/** Ordertijdlijn (FR18): chronologisch, alle diensten/producten van deze order. */
export async function getOrderTimeline(orderNumber: string): Promise<TimelineEnvelope> {
  const response = await apiClient.get<TimelineEnvelope>(`/orders/${encodeURIComponent(orderNumber)}/timeline`);
  return response.data;
}

/** GTIN-tijdlijn (FR19): de volledige levensloop van één trade item over alle diensten. */
export async function getTradeItemTimeline(tradeItemId: string): Promise<TimelineEnvelope> {
  const response = await apiClient.get<TimelineEnvelope>(`/trade-items/${encodeURIComponent(tradeItemId)}/timeline`);
  return response.data;
}

export interface LeadtimeTransition {
  from: string;
  to: string;
  observations: number;
  insufficientData: boolean;
  averageMs: number | null;
  stddevMs: number | null;
}

export interface LeadtimeAnalysisResult {
  serviceCode: string;
  window: { from?: string; to?: string };
  minObservations: number;
  transitions: LeadtimeTransition[];
}

export interface LeadtimeAnalysisEnvelope {
  data: LeadtimeAnalysisResult | null;
  meta: { degraded: boolean };
}

export interface LeadtimeWindow {
  from?: string;
  to?: string;
}

/** FR19a: doorlooptijd-analyse per stap-overgang voor één dienst. */
export async function getLeadtimeAnalysis(serviceCode: string, window: LeadtimeWindow = {}): Promise<LeadtimeAnalysisEnvelope> {
  const query = new URLSearchParams({ serviceCode });
  if (window.from != null) query.set('from', window.from);
  if (window.to != null) query.set('to', window.to);
  const response = await apiClient.get<LeadtimeAnalysisEnvelope>(`/orders/analytics/leadtime?${query.toString()}`);
  return response.data;
}

export interface CoverageResult {
  serviceCode: string;
  observedTradeItems: number;
  expectedTradeItems: number;
  coveragePercent: number;
}

export interface CoverageEnvelope {
  data: CoverageResult[] | null;
  meta: { degraded: boolean };
}

/** FR20/M3: event-dekkingsoverzicht. Without a serviceCode, reports every known dienst. */
export async function getCoverage(serviceCode?: string): Promise<CoverageEnvelope> {
  const query = serviceCode != null ? `?serviceCode=${encodeURIComponent(serviceCode)}` : '';
  const response = await apiClient.get<CoverageEnvelope>(`/orders/analytics/coverage${query}`);
  return response.data;
}
